import type { Item } from "anketa-core/base";
import { ComplexType } from "anketa-core/base/complex";
import { Form } from "anketa-core/complex";
import { OpenQuestion } from "anketa-core/open_question";
import { Translation } from "anketa-core/i18n";
import { TextValidator } from "anketa-core/validation";
import { TextValidatorType } from "anketa-core/base/types";

/**
 * Customer contact person, modelled as an anketa-core complex type so it is
 * rendered and persisted through the standard questionnaire machinery (the
 * generic ComplexQuestion form editor renders this `getForm()` in a modal).
 *
 * Used for the per-role customer contacts (Sales / Technical / Organizational)
 * in the General section. On save, the e-mail is provisioned as a *managed*
 * user in the customer's Keycloak organization (organizations are keyed by
 * mail domain, so only e-mails whose domain has a customer organization are
 * created - enforced server-side in
 * functions/questionnaires/catalog-action/contacts.ts).
 */
export class CustomerContact extends ComplexType {
  firstName?: string;
  surname?: string;
  email?: string;
  phone?: string;
  jobTitle?: string;

  constructor(init?: any) {
    super("CustomerContact", init);
  }

  static getForm(): Item {
    const form = new Form("CustomerContact", [
      new Translation("en", "Customer contact"),
      new Translation("de", "Kundenkontakt"),
    ]);
    const firstname = new OpenQuestion("firstName", [
      new Translation("en", "First name"),
      new Translation("de", "Vorname"),
    ]);
    firstname.validator = new TextValidator();

    form.addChild(firstname);
    const surname = new OpenQuestion("surname", [
      new Translation("en", "Last name"),
      new Translation("de", "Nachname"),
    ]);
    surname.validator = new TextValidator();

    form.addChild(surname);

    const email = new OpenQuestion("email", [
      new Translation("en", "E-mail"),
      new Translation("de", "E-Mail"),
    ]);
    email.required = true;
    email.validator = new TextValidator(TextValidatorType.CustomerEmail);
    form.addChild(email);

    const phone = new OpenQuestion("phone", [
      new Translation("en", "Phone"),
      new Translation("de", "Telefon"),
    ]);
    phone.validator = new TextValidator(TextValidatorType.PhoneNumber);
    form.addChild(phone);

    const title = new OpenQuestion("jobTitle", [
      new Translation("en", "Job title"),
      new Translation("de", "Position"),
    ]);
    title.validator = new TextValidator();
    form.addChild(title);

    return form;
  }
}
